// AiBuddyVoiceLanes — per-bubble "listen" buttons for the AI Assistant.
// Jun 2026 (Randy): UNIVERSAL two-voice scheme — the question is read in
// questionVoice, the answer in answerVoice — identical to the public Q&A and the
// top "Listen to whole conversation" button, so the cache hits (and the green
// "saved" state) are SHARED across every button. The green state is driven by the
// parent's single `coverage` source, never a per-button probe.
//   • Premium — premium OpenAI voice, billed to the creator's own key/voucher.
//   • Free    — the device's built-in browser voice (no key, no cost).
// Tapping while it's playing PAUSES (resumes from where it left off); a third
// element is the voice-selection dropdown.
import { useRef } from "react";
import { VOICE_OPTIONS } from "@/lib/useVoiceQA";
import { SpeakerHigh, SpeakerSimpleHigh, Pause, Play, Stop } from "@phosphor-icons/react";

export default function AiBuddyVoiceLanes({
  voice, text, msgKey, question = null, answerVoice, questionVoice, setAnswerVoice,
  cached = false, aIdx = "buddy", qIdx = "buddy-q", onScope,
}) {
  const paused = voice.state === "paused";
  const playingOrPaused = voice.state === "speaking" || voice.state === "processing" || paused;
  const paid = !!cached;     // single source of truth = parent coverage
  // Cost estimate covers the question + answer (both get read / cached).
  const segText = question ? `${question}\n\n${text}` : (text || "");
  const rawChars = segText.replace(/\s+/g, " ").trim().length;
  const estCents = rawChars * 0.0015; // 1× raw tts-1 (own key) estimate
  const costLabel = estCents < 1 ? `${estCents.toFixed(1)}¢` : `${Math.round(estCents)}¢`;
  const sz = 13;

  // Each lane owns its own playback "key" so re-pressing THIS button pauses /
  // resumes it, while pressing any other control starts fresh.
  const premiumKey = `seg:${msgKey}:self`;
  const freeKey = `seg:${msgKey}:device`;
  const premiumActive = playingOrPaused && voice.playKey === premiumKey;
  const freeActive = playingOrPaused && voice.playKey === freeKey;

  // Two-voice turns: question (questionVoice) → answer (answerVoice), matching the
  // whole-conversation premium read so the cache hits are shared between buttons.
  const turnsFor = () =>
    question != null
      ? [{ role: "user", text: question, idx: qIdx }, { role: "assistant", text, idx: aIdx }]
      : [{ role: "assistant", text, idx: aIdx }];
  // Tell the parent this exchange is now what's playing, so the whole-conversation
  // scrubber narrows to it (and a long question can be skipped). Sep 2026 (Randy).
  const playSeg = (mode, key) => {
    onScope?.(key, mode);
    return voice.speakConversation(turnsFor(), { answerVoice, questionVoice, mode, key });
  };

  const lastPremiumTap = useRef(0);
  const onPremium = async () => {
    if (premiumActive) {
      // Double-tap restarts from the beginning; single tap pauses / resumes.
      const now = Date.now();
      if (now - lastPremiumTap.current < 350) {
        lastPremiumTap.current = 0;
        voice.stopSpeaking();
        await playSeg("self", premiumKey);
        return;
      }
      lastPremiumTap.current = now;
      voice.pauseOrResume();
      return;
    }
    await playSeg("self", premiumKey);
  };
  const onFree = () => {
    // FREE (device) voice: single tap STOPS reliably (iOS pause is unreliable);
    // tap again to replay from the beginning. Question a touch higher, answer lower.
    if (freeActive) { voice.stopSpeaking(); return; }
    const segs = question != null
      ? [{ text: question, pitch: 1.18, idx: qIdx }, { text, pitch: 0.92, idx: aIdx }]
      : [{ text, pitch: 1, idx: aIdx }];
    onScope?.(freeKey, "device");
    voice.speakDevice(segs, { key: freeKey });
  };

  const iconFor = (isActive, fallback) =>
    isActive ? (paused ? <Play size={sz} weight="fill" /> : <Pause size={sz} weight="fill" />) : fallback;

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="ai-buddy-voice-lanes">
      <button
        type="button"
        data-testid="ai-buddy-voice-premium"
        onClick={onPremium}
        title={paid ? "Saved (free replay for 24h)" : `Premium voice — about ${costLabel}`}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold text-white hover:opacity-90 transition-opacity ${voice.warming && premiumActive ? "voice-warming" : ""}`}
        style={{ background: paid ? "#27AE60" : "#4A5D4E" }}
      >
        {iconFor(premiumActive, <SpeakerHigh size={sz} weight="fill" />)}
        {paid ? "Saved" : `Premium ~${costLabel}`}
      </button>
      <button
        type="button"
        data-testid="ai-buddy-voice-free"
        onClick={onFree}
        title="Free device voice"
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border hairline bg-white text-[10px] font-semibold text-[#1C1B1A] hover:bg-[#F0F1EE] transition-colors"
      >
        {freeActive ? <Stop size={sz} weight="fill" /> : <SpeakerSimpleHigh size={sz} weight="fill" />}
        Free
      </button>
      <select
        value={answerVoice}
        onChange={(e) => setAnswerVoice(e.target.value)}
        data-testid="ai-buddy-voice-select"
        className="text-[10px] rounded-md border hairline bg-white px-1.5 py-1 text-[#4A5D4E]"
      >
        {VOICE_OPTIONS.map((o) => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}
